/**
 * BullseyeTarget — the SVG target: rings, one slice per active list, a dot per
 * reviewed person, the list labels around it and the ring labels on top.
 *
 * It draws what `layoutBullseye` computed and holds no maths of its own.
 * Geometry rides in SVG attributes; colours are token utilities on the
 * elements (`fill-tenure-*`, `fill-chart-*`), never a `style` prop.
 *
 * The hover card is pinned to the corner AWAY from the dot (the same rule as
 * `ChartTooltip`), so it never covers the dot it describes and needs no
 * computed position. Present mode turns hover off entirely: a client on the
 * other end of a screen share must not read names or scores.
 *
 * The card shows the person's stored photo (initials while it loads or when
 * there is none). Its URL is built only when the card opens for that person,
 * through the same `useStoredImageUrls` People Lists uses: a CloudFront URL
 * with no request when the media session serves avatars, otherwise a presign
 * of that ONE key, kept by React Query under the person's key so hovering
 * them again asks nothing. The other dots ask for nothing. The presign waits
 * `PHOTO_HOVER_INTENT_MS` of resting on the dot, so a pointer sweeping across
 * a crowded slice asks for no photo at all.
 */
import type { BullseyeList, BullseyePerson } from "@nu-assist/contracts";
import { Avatar } from "@nu-assist/ui";
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { useMediaSession } from "../../../../features/media/MediaSessionProvider";
import { servesKind } from "../../../../features/media/mediaUrls";
import { useStoredImageUrls } from "../../../../features/media/useStoredImageUrls";
import {
  type BullseyeLayout,
  CX,
  CY,
  DIVIDER_WIDTH,
  R,
  type RingModel,
  VIEW_H,
  VIEW_W,
  dotOutline,
  f2,
  missingCount,
  ringTone,
  separatorWidth,
} from "./bullseyeGeometry";
import { type ListTone, RING_FILL } from "./bullseyeTones";

export interface PlottedList {
  list: BullseyeList;
  /** The list's display name (People Lists' `displayTitle`). */
  title: string;
  tone: ListTone;
}

export interface BullseyeTargetProps {
  lists: readonly PlottedList[];
  model: RingModel;
  layout: BullseyeLayout;
  /** The focused list's review id, or `null`. */
  focus: string | null;
  onToggleFocus: (listId: string) => void;
  present: boolean;
  footer: ReactNode;
  /** Presigns stored photo keys when CloudFront does not serve avatars
   *  (`AvatarsClient.presignAvatars`); called for a hovered person only. */
  presignPhotos?: (keys: string[]) => Promise<Record<string, string>>;
}

/** React Query key prefix of a hovered person's presigned photo. */
const PHOTO_PRESIGN_QUERY_KEY = "bullseye-photo-presign";

/** How long the pointer rests on a dot before its photo is presigned. */
export const PHOTO_HOVER_INTENT_MS = 150;

interface Hovered {
  listId: string;
  personId: string;
  side: "left" | "right";
}

const SCORE_CHIP: Record<1 | 2 | 3, string> = {
  3: "bg-accent-muted text-accent-ink",
  2: "bg-surface-raised text-text-secondary",
  1: "border border-border text-text-muted",
};

export function BullseyeTarget({
  lists,
  model,
  layout,
  focus,
  onToggleFocus,
  present,
  footer,
  presignPhotos,
}: BullseyeTargetProps) {
  const hatchId = `bullseye-hatch-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [hovered, setHovered] = useState<Hovered | null>(null);
  // Photos already asked for while this target is mounted: hovering the
  // person again shows theirs at once, from React Query's cache.
  const askedPhotos = useRef(new Set<string>()).current;
  const n = model.count;
  const multi = lists.length > 1;
  const byId = useMemo(() => new Map(lists.map((p) => [p.list.ai_review_id, p])), [lists]);
  const personOf = useMemo(() => {
    const out = new Map<string, Map<string, BullseyePerson>>();
    for (const { list } of lists)
      out.set(list.ai_review_id, new Map(list.people.map((p) => [p.import_contact_id, p])));
    return out;
  }, [lists]);

  const dim = useCallback((listId: string) => focus !== null && focus !== listId, [focus]);

  const onDotOver = useCallback(
    (e: ReactPointerEvent<SVGGElement>) => {
      if (present) return;
      const node = e.target as Element;
      const listId = node.getAttribute("data-list");
      const personId = node.getAttribute("data-person");
      if (!listId || !personId) return;
      const cx = Number(node.getAttribute("cx"));
      setHovered({ listId, personId, side: cx < CX ? "right" : "left" });
    },
    [present]
  );

  // Leaving a dot clears the card; moving onto the next dot sets it again
  // (`pointerout` fires before the next `pointerover`).
  const onDotOut = useCallback(() => setHovered(null), []);

  // The dot layers are memoised so a hover (which only changes `hovered`)
  // does not re-render ~1.4k circles: React skips an identical element.
  const dotLayers = useMemo(() => {
    return (
      <>
        <g data-testid="bullseye-outside" onPointerOver={onDotOver} onPointerOut={onDotOut}>
          {layout.outsideDots.map((d) => (
            <circle
              key={`out-${d.listId}-${d.personId}`}
              cx={d.x}
              cy={d.y}
              r={d.r}
              fill="none"
              strokeWidth={1.4}
              opacity={dim(d.listId) ? 0.25 : 0.85}
              className={byId.get(d.listId)?.tone.stroke}
              data-list={d.listId}
              data-person={d.personId}
            />
          ))}
        </g>
        <g
          data-testid="bullseye-dots"
          onPointerOver={onDotOver}
          onPointerOut={onDotOut}
          className="stroke-bg light:stroke-text"
        >
          {layout.dots.map((d) => (
            <circle
              key={`dot-${d.listId}-${d.personId}`}
              cx={d.x}
              cy={d.y}
              r={d.r}
              strokeWidth={dotOutline(d.r)}
              opacity={dim(d.listId) ? 0.25 : 1}
              className={byId.get(d.listId)?.tone.fill}
              data-list={d.listId}
              data-person={d.personId}
            />
          ))}
        </g>
      </>
    );
  }, [layout, byId, dim, onDotOver, onDotOut]);

  const hoveredList = hovered ? byId.get(hovered.listId) : undefined;
  const hoveredPerson = hovered ? personOf.get(hovered.listId)?.get(hovered.personId) : undefined;

  return (
    <div
      className="relative rounded-md border border-border bg-surface p-3 shadow-sm"
      data-testid="bullseye-stage"
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="mx-auto block h-auto w-full max-w-5xl"
        data-testid="bullseye-target"
        onPointerLeave={() => setHovered(null)}
      >
        <title>Bullseye of AI Review results, one slice per list</title>
        <defs>
          <pattern
            id={hatchId}
            width={7}
            height={7}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width={7} height={7} className="fill-surface" />
            <line x1={0} y1={0} x2={0} y2={7} strokeWidth={3} className="stroke-border" />
          </pattern>
        </defs>

        {layout.radii
          .map((r, b) => ({ r, b }))
          .reverse()
          .map(({ r, b }) => (
            <circle
              key={`ring-${b}`}
              cx={CX}
              cy={CY}
              r={r}
              className={RING_FILL[ringTone(b, n)]}
              data-testid="bullseye-ring"
            />
          ))}
        {layout.radii.slice(0, -1).map((r) => (
          <circle
            key={`sep-${r}`}
            cx={CX}
            cy={CY}
            r={r}
            fill="none"
            strokeWidth={separatorWidth(n)}
            className="stroke-surface"
          />
        ))}

        {layout.slices.map((s) =>
          s.hatch ? (
            <path
              key={`hatch-${s.listId}`}
              d={s.hatch}
              fill={`url(#${hatchId})`}
              fillRule="evenodd"
              opacity={0.9}
              data-testid="bullseye-hatch"
              data-list={s.listId}
            />
          ) : null
        )}

        {lists.length === 0 && (
          <>
            <circle cx={CX} cy={CY} r={R} opacity={0.75} className="fill-surface" />
            <text
              x={CX}
              y={CY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={15}
              className="fill-text-secondary"
            >
              Turn on an AI Review list to plot it
            </text>
          </>
        )}

        {layout.slices.map((s) => (
          <g key={`slice-${s.listId}`}>
            {dim(s.listId) && <path d={s.wedge} opacity={0.6} className="fill-surface" />}
            {multi && (
              <path
                d={s.wedge}
                fill="transparent"
                role="button"
                tabIndex={0}
                aria-pressed={focus === s.listId}
                aria-label={`Focus ${byId.get(s.listId)?.title ?? "list"}`}
                strokeWidth={3}
                className="cursor-pointer stroke-transparent focus-visible:stroke-accent focus-visible:outline-none"
                data-testid="bullseye-slice"
                data-list={s.listId}
                onClick={() => onToggleFocus(s.listId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleFocus(s.listId);
                  }
                }}
              />
            )}
          </g>
        ))}
        {multi &&
          layout.slices.map((s) => (
            <line
              key={`div-${s.listId}`}
              x1={CX}
              y1={CY}
              x2={s.divider[0]}
              y2={s.divider[1]}
              strokeWidth={DIVIDER_WIDTH}
              className="pointer-events-none stroke-surface"
            />
          ))}

        {layout.slices.map((s) => {
          const tone = byId.get(s.listId)?.tone;
          return (
            <path
              key={`arc-${s.listId}`}
              d={s.arc}
              fill="none"
              strokeWidth={6}
              strokeLinecap="round"
              opacity={dim(s.listId) ? 0.3 : 1}
              className={tone?.stroke}
            />
          );
        })}

        {layout.labels.map((l) => (
          <g key={`label-${l.listId}`} data-testid="bullseye-list-label" data-list={l.listId}>
            <text
              x={l.x}
              y={l.y}
              textAnchor={l.anchor}
              fontSize={14}
              fontWeight={700}
              opacity={dim(l.listId) ? 0.4 : 1}
              className="fill-text"
            >
              {l.lines.map((line, i) => (
                <tspan key={`${l.listId}-${i}`} x={l.x} dy={i ? 17 : 0}>
                  {line}
                </tspan>
              ))}
            </text>
            <text
              x={l.x}
              y={l.countsY}
              textAnchor={l.anchor}
              fontSize={12}
              className="fill-text-secondary"
            >
              {l.counts.join(" · ")}
            </text>
          </g>
        ))}

        {dotLayers}

        {layout.ringLabels.map((l) => (
          <text
            key={`rl-${l.text}`}
            x={CX}
            y={l.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={11}
            fontWeight={700}
            strokeWidth={3.5}
            paintOrder="stroke"
            className="pointer-events-none fill-text stroke-surface"
            data-testid="bullseye-ring-label"
          >
            {l.text}
          </text>
        ))}
      </svg>

      <div
        className="min-h-5 text-center text-body text-text-secondary"
        data-testid="bullseye-footer"
      >
        {footer}
      </div>

      {!present && hovered && hoveredList && hoveredPerson && (
        <HoverCard
          key={`${hovered.listId}:${hovered.personId}`}
          list={hoveredList}
          person={hoveredPerson}
          side={hovered.side}
          presignPhotos={presignPhotos}
          askedPhotos={askedPhotos}
        />
      )}
    </div>
  );
}

function HoverCard({
  list,
  person,
  side,
  presignPhotos,
  askedPhotos,
}: {
  list: PlottedList;
  person: BullseyePerson;
  side: "left" | "right";
  presignPhotos: BullseyeTargetProps["presignPhotos"];
  askedPhotos: Set<string>;
}) {
  const photoKey = person.profile_image_s3_key;
  const { settled, session } = useMediaSession();
  // CloudFront builds the URL with no request, and a photo already asked for
  // is cached: neither needs to wait for the pointer to settle.
  const [intent, setIntent] = useState(
    () => (settled && servesKind(session, "avatars")) || (!!photoKey && askedPhotos.has(photoKey))
  );
  useEffect(() => {
    if (intent || !photoKey) return;
    const t = setTimeout(() => setIntent(true), PHOTO_HOVER_INTENT_MS);
    return () => clearTimeout(t);
  }, [intent, photoKey]);
  useEffect(() => {
    if (intent && photoKey) askedPhotos.add(photoKey);
  }, [intent, photoKey, askedPhotos]);
  const keys = useMemo(() => (intent && photoKey ? [photoKey] : []), [intent, photoKey]);
  // The photo's capture time as `?v=`: a re-captured photo is rewritten under
  // the same key (epic 2026-10-23-media-url-versions).
  const photoVersion = person.profile_image_captured_at;
  const versions = useMemo(
    () => (photoKey && photoVersion != null ? new Map([[photoKey, photoVersion]]) : undefined),
    [photoKey, photoVersion]
  );
  const { urls } = useStoredImageUrls(
    "avatars",
    keys,
    presignPhotos,
    PHOTO_PRESIGN_QUERY_KEY,
    versions
  );
  const name = person.display_name || "Unnamed contact";
  const missing = missingCount(person.scores);
  const total = list.list.criteria.length;
  return (
    <div
      data-testid="bullseye-hover"
      aria-hidden="true"
      className={`pointer-events-none absolute top-3 z-10 w-64 rounded-md border border-border bg-surface px-3 py-2.5 text-caption text-text shadow-md ${
        side === "left" ? "left-3" : "right-3"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <Avatar src={photoKey ? urls.get(photoKey) : null} name={person.display_name} size="md" />
        <div className="min-w-0">
          <p className="text-label font-semibold text-text">{name}</p>
          <p className="flex items-center gap-1.5 text-text-secondary">
            <span className={`inline-block h-2 w-2 rounded-full ${list.tone.bg}`} />
            {list.title}
          </p>
        </div>
      </div>
      <p className="mt-1 font-semibold tabular-nums text-text">
        AI score {f2(person.weighted_total)} ·{" "}
        {missing === 0 ? "all criteria met" : `missing ${missing} of ${total}`}
      </p>
      <ul className="mt-1.5 flex flex-col gap-0.5">
        {list.list.criteria.map((c, i) => {
          const score = person.scores[i] ?? null;
          return (
            <li key={`${c.name}-${i}`} className="flex items-start gap-1.5">
              <span
                className={`w-4 shrink-0 rounded-xs text-center text-micro font-bold tabular-nums ${
                  score === null ? "text-text-muted" : SCORE_CHIP[score]
                }`}
              >
                {score ?? "–"}
              </span>
              <span>{c.name}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
